// AI-ASSISTED: generated with claude-code on 2026-06-10, reviewed by Yoann Hertienne
import useAsync from 'react-use/lib/useAsync';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { TileBoundary } from '../team-landing/TileBoundary';

const FEED_ANNOTATION = 'dev.azure.com/artifact-feed';
const PACKAGE_ANNOTATION = 'dev.azure.com/package-name';
const ORG_ANNOTATION = 'dev.azure.com/org';
const DEFAULT_ORG = 'LuminusITDigital';

type ParsedRelease = {
  version: string;
  publishedAt?: string;
};

export const parseLatestRelease = (payload: unknown): ParsedRelease | undefined => {
  if (!payload || typeof payload !== 'object') return undefined;
  const meta = payload as Record<string, unknown>;
  const distTags = meta['dist-tags'] as Record<string, string> | undefined;
  const latest = distTags?.latest;
  if (!latest) return undefined;
  const time = meta.time as Record<string, string> | undefined;
  return { version: latest, publishedAt: time?.[latest] };
};

const AdoArtifactsCardBody = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const annotations = entity.metadata.annotations ?? {};
  const feed = annotations[FEED_ANNOTATION];
  const packageName = annotations[PACKAGE_ANNOTATION];
  const org = annotations[ORG_ANNOTATION] ?? DEFAULT_ORG;

  const { value, loading, error } = useAsync(async () => {
    if (!feed || !packageName) return undefined;
    const baseUrl = await discoveryApi.getBaseUrl('proxy');
    const encodedPkg = encodeURIComponent(packageName).replace('%2F', '/');
    const url = `${baseUrl}/ado-artifacts/${encodeURIComponent(org)}/_packaging/${encodeURIComponent(
      feed,
    )}/npm/registry/${encodedPkg}`;
    const response = await fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(`ADO Artifacts request failed: ${response.status}`);
    }
    return parseLatestRelease(await response.json());
  }, [feed, packageName, org]);

  if (!feed || !packageName) return null;
  if (error) throw error;

  const release = value;
  const feedUrl = `https://dev.azure.com/${encodeURIComponent(
    org,
  )}/_artifacts/feed/${encodeURIComponent(feed)}`;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6">Azure Artifacts (npm)</Typography>
        <Typography variant="body2" color="text.secondary">
          {packageName}
        </Typography>
        <Box sx={{ pt: 1 }}>
          {loading && (
            <CircularProgress size={20} aria-label="Loading latest Azure Artifacts version" />
          )}
          {!loading && release && (
            <Stack direction="row" spacing={1}>
              <Chip size="small" color="primary" label={`latest: ${release.version}`} />
              {release.publishedAt && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`published ${release.publishedAt.slice(0, 10)}`}
                />
              )}
            </Stack>
          )}
          {!loading && !release && (
            <Typography variant="body2" color="text.secondary">
              No release info available.
            </Typography>
          )}
        </Box>
      </CardContent>
      <CardActions>
        <Button
          variant="outlined"
          size="small"
          href={feedUrl}
          target="_blank"
          rel="noreferrer"
          aria-label="Open Azure Artifacts feed for this component"
        >
          Open feed
        </Button>
      </CardActions>
    </Card>
  );
};

export const AdoArtifactsCard = () => (
  <TileBoundary tool="Azure Artifacts">
    <AdoArtifactsCardBody />
  </TileBoundary>
);
